import { DatabaseView } from '@databases/types/database/view';
import { Context } from '@databases/types/redux';
import { logWarning } from '@databases/utils/utils';

import { DatasourceService } from '@canvas/atlassian-service/datasource';
import { getServerIdFromCloudId } from '@canvas/atlassian-service/datasource/migration';
import { getIdentity } from '@canvas/connection/identity';

export const autoCompleteLabel = async (query: string): Promise<string[]> => {
  const response = await fetch(
    `/wiki/labels/autocompletelabel.action?${new URLSearchParams({
      query,
    })}`
  );

  if (!response.ok) {
    logWarning(
      new Error('Could not autocomplete labels', {
        cause: `Request failed: ${response.url}. Status: ${response.status}.`,
      })
    );
    return [];
  } else {
    const data = await response.json();
    return (data.contentNameMatches as any[][]).flatMap((m) => m.map((l) => l.name));
  }
};

/**
 * Thin wrapper around fetch API that takes care to pass in the baseURL
 */
export const fetchData = async (path: string, init?: RequestInit | undefined) => {
  const baseURL = getBaseUrl().toString();
  return await fetch(`${baseURL}${path}`, init);
};

export const getCloudId = (): string | undefined => {
  const queryParams = new URLSearchParams(window.location.search);
  const cloudId = queryParams.get('cloudId');

  return cloudId ?? undefined;
};

/**
 * @throws Error if spaceKey is not defined
 */
export const getSpaceKey = (): string => {
  const queryParams = new URLSearchParams(window.location.search);
  if (queryParams.has('spaceKey')) {
    return queryParams.get('spaceKey') as string;
  }

  throw new Error('spaceKey is not defined');
};

export const getBaseUrl = (): string => {
  return window.location.origin + '/wiki';
};

/**
 * @throws Error if contentId is not defined
 */
export const getContentId = (): string => {
  const queryParams = new URLSearchParams(window.location.search);
  const contentId = queryParams.get('contentId');
  if (contentId) {
    return contentId;
  }

  throw new Error('contentId is not defined');
};

export const getActivationId = (): string | undefined => {
  const queryParams = new URLSearchParams(window.location.search);
  const activationId = queryParams.get('activationId');

  return activationId ?? undefined;
};

export const getConfluenceWorkspaceId = (): string => {
  const cloudId = getCloudId();
  const activationId = getActivationId();

  return cloudId && activationId
    ? `ari:cloud:confluence:${cloudId}:workspace/${activationId}`
    : '';
};

export const getContext = async (): Promise<Context> => {
  const [space, baseUrl, userId] = await Promise.all([
    {
      key: getSpaceKey(),
    },
    getBaseUrl(),
    (await getIdentity()).accountId,
  ]);

  return { space, baseUrl, userId };
};

export const getCustomProperty = async (
  contentId: string,
  property: string
): Promise<DatabaseView | null> => {
  const response = await fetch(
    `/wiki/rest/api/content/${contentId}/property/${property}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.ok) {
    const data: { value: DatabaseView } = await response.json();
    return data.value;
  }
  return null;
};

export const getJiraServers = async () => {
  const sites = await new DatasourceService('jira').getAvailableSites();
  return await Promise.all(
    sites.map(async (site) => {
      const { name, cloudId } = site;
      const id = await getServerIdFromCloudId(cloudId);
      return { id, name, cloudId };
    })
  );
};
